import moment from "moment";
import { GetLocalStorage } from "./cryptography";
import { AUTimeFormat } from "./date";
import { GetLangKey, Language } from "./general";
import { t } from "i18next";
import { IsNumeric } from "./field-validation";

export enum ProfileTemplate {
  PURE_HARMONY = 1,
  HERB_ESSENCE = 2,
}

export enum TimeSlotStyle {
  DROPDOWN = 0,
  SWIPE = 1,
}

export enum TemplateModal {
  CLOSE,
  SLUG,
  TITLE,
  NAME,
  INTRODUCTION,
  AVATAR,
  SKILL,
  CLINIC,
  DESCRIPTION,
  VIDEO,
  STRENGTHS = "strengths",
  SPECIALISE = "specialise",
  COURSE = "course",
  ARTICLE = "article",
  REVIEW = "review",
  REVIEW_BG = "reviewBg",
  COLLEAGUE = "colleague",
  DAILY_SHARING = "dailySharing",
}

export enum ContentLimit {
  TITLE = 50,
  FIRST_NAME = 15,
  LAST_NAME = 15,
  SLUG = 35,
  INTRODUCTION = 180,
  SKILL = 25,
  DESCRIPTION = 350,
  STRENGTH_TITLE = 40,
  STRENGTH = 150,
  SPECIALISE_TITLE = 40,
  SPECIALITY = 150,
  COURSE_TITLE = 50,
  COURSE = 200,
  COURSE_LOCATION = 50,
  ARTICLE_TITLE = 60,
  ARTICLE = 200,
  PATIENT_NAME = 50,
  PATIENT_DESCRIPTION = 100,
  REVIEW = 400,
  COLLEAGUE_NAME = 50,
  COLLEAGUE_DESCRIPTION = 400,
  DAILY_SHARING = 500,
  VIDEO_TITLE = 50,
  VIDEO_DESCRIPTION = 200,
}

export enum ContentCountLimit {
  SKILL_KEY = 6,
  STRENGTH = 6,
  SPECIALITY = 6,
  COURSE = 6,
  REVIEW = 3,
  ARTICLE = 9,
  COLLEAGUE = 8,
  DAILY_SHARING_IMAGE = 9,
  DAILY_SHARING = 10,
  VIDEO = 6,
}

export enum TitleOption {
  ACUPUNCTURIST,
  MASSEUR,
  CN_MEDICINE_PRACTITIONER,
}

export enum ProfilePlaceholder {
  TITLE = "EG_PROFESSOR_OF_TRADITIONAL_CHINESE_MEDICINE",
  INTRODUCTION = "PLEASE_BRIEFLY_INTRODUCE_YOURSELF",
  SKILL = "HERBAL_MEDICINE",
  DESCRIPTION = "TRADITIONAL_CHINESE_MEDICINE_IS_A_DISCIPLINE_THAT_UTILIZES_NATURAL_PRINCIPLES_COMBINED_WITH_VARIOUS_PHYSIOLOGICAL_AND_PATHOLOGICAL_CHANGES_IN_THE_BODY",
}
export const LANG_MULTIPLIER = 0.9;
export const GetCNStrLimit = (initialLimit: ContentLimit) => {
  let multiplier = GetLangKey() == Language.ZH_CN ? LANG_MULTIPLIER : 1;
  // @ts-ignore
  return parseInt(initialLimit * multiplier);
};

export const GetCroppedImg = (imageSrc: string, croppedAreaPixels: any) => {
  //Circle Crop
  // return new Promise((resolve, reject) => {
  //   const image = new Image();
  //   image.src = imageSrc;

  //   image.onload = () => {
  //     const canvas = document.createElement("canvas");
  //     const ctx: any = canvas.getContext("2d");

  //     const size = Math.max(croppedAreaPixels.width, croppedAreaPixels.height);
  //     canvas.width = size;
  //     canvas.height = size;

  //     // Clip into a circle
  //     ctx.beginPath();
  //     ctx.arc(size / 2, size / 2, size / 2, 0, 2 * Math.PI);
  //     ctx.closePath();
  //     ctx.clip();

  //     ctx.drawImage(
  //       image,
  //       croppedAreaPixels.x,
  //       croppedAreaPixels.y,
  //       croppedAreaPixels.width,
  //       croppedAreaPixels.height,
  //       0,
  //       0,
  //       size,
  //       size
  //     );

  //     const base64Image = canvas.toDataURL("image/png");
  //     const base64WithoutPrefix = base64Image.replace(/^data:.*;base64,/, "");
  //     resolve(base64WithoutPrefix);
  //   };

  //   image.onerror = (error) => reject(error);
  // });
  // Rectangle Crop
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.src = imageSrc;

    image.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      if (!ctx) {
        reject(new Error("Failed to get 2D context"));
        return;
      }

      // 🟦 LOGO MODE: keep the rectangular crop
      canvas.width = croppedAreaPixels.width;
      canvas.height = croppedAreaPixels.height;

      // ❗ no arc/clip here → no rounding
      ctx.drawImage(
        image,
        croppedAreaPixels.x,
        croppedAreaPixels.y,
        croppedAreaPixels.width,
        croppedAreaPixels.height,
        0,
        0,
        croppedAreaPixels.width,
        croppedAreaPixels.height
      );

      const base64Image = canvas.toDataURL("image/png");
      const base64WithoutPrefix = base64Image.replace(/^data:.*;base64,/, "");
      resolve(base64WithoutPrefix);
    };

    image.onerror = (error) => reject(error);
  });
};

export const GetCroppedRectImg = (imageSrc: string, croppedAreaPixels: any) => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.src = imageSrc;

    image.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      if (!ctx) {
        reject(new Error("Failed to get 2D context"));
        return;
      }

      // 🟦 LOGO MODE: keep the rectangular crop
      canvas.width = croppedAreaPixels.width;
      canvas.height = croppedAreaPixels.height;

      // ❗ no arc/clip here → no rounding
      ctx.drawImage(
        image,
        croppedAreaPixels.x,
        croppedAreaPixels.y,
        croppedAreaPixels.width,
        croppedAreaPixels.height,
        0,
        0,
        croppedAreaPixels.width,
        croppedAreaPixels.height
      );

      const base64Image = canvas.toDataURL("image/png");
      const base64WithoutPrefix = base64Image.replace(/^data:.*;base64,/, "");
      resolve(base64WithoutPrefix);
    };

    image.onerror = (error) => reject(error);
  });
};

export const GetEarlyStartTime = (clinic: any) => {
  const sortEarliestStartTime = clinic.workingTimes
    .filter((w: any) => w.isWorking)
    .sort((a: any, b: any) =>
      moment(a.startTime, "HH:mm:ss").isBefore(moment(b.startTime, "HH:mm:ss"))
        ? -1
        : 1
    );
  return moment(sortEarliestStartTime[0]?.startTime, "HH:mm:ss").format(
    AUTimeFormat
  );
};

export const GetLatestEndTime = (clinic: any) => {
  const sortLatestEndTime = clinic.workingTimes
    .filter((w: any) => w.isWorking)
    .sort((a: any, b: any) =>
      moment(a.endTime, "HH:mm:ss").isBefore(moment(b.endTime, "HH:mm:ss"))
        ? 1
        : -1
    );
  return moment(sortLatestEndTime[0]?.endTime, "HH:mm:ss").format(AUTimeFormat);
};

export const GetActionModalTitle = (modal: TemplateModal) => {
  switch (modal) {
    case TemplateModal.TITLE:
      return t("EDIT_TITLE");
    case TemplateModal.NAME:
      return t("EDIT_NAME");
    case TemplateModal.SLUG:
      return t("EDIT_SLUG");
    case TemplateModal.INTRODUCTION:
      return t("EDIT_INTRODUCTION");
    case TemplateModal.SKILL:
      return t("ADD_SKILL");
    case TemplateModal.AVATAR:
      return t("EDIT_AVATAR");
    case TemplateModal.CLINIC:
      return t("EDIT_CLINIC");
    case TemplateModal.DESCRIPTION:
      return t("EDIT_DESCRIPTION");
    case TemplateModal.STRENGTHS:
      return t("EDIT_STRENGTHS");
    case TemplateModal.SPECIALISE:
      return t("EDIT_PROFILE_TREATMENT");
    case TemplateModal.COURSE:
      return t("EDIT_COURSE");
    case TemplateModal.ARTICLE:
      return t("EDIT_ARTICLE");
    case TemplateModal.REVIEW:
      return t("EDIT_PATIENT_REVIEW");
    case TemplateModal.REVIEW_BG:
      return t("UPLOAD_REVIEW_BG");
    case TemplateModal.COLLEAGUE:
      return t("EDIT_MY_COLLEAGUES");
    case TemplateModal.DAILY_SHARING:
      return t("EDIT_DAILY_SHARING");
    case TemplateModal.VIDEO:
      return t("EDIT_VIDEO");
    default:
      return "";
  }
};

export const ConstructerdLocalElementID = (id: string) => `LOCAL_${id}`;
export const IsLocalElement = (id: string) => {
  if (IsNumeric(id)) return false;
  return id.startsWith("LOCAL_");
};

export const EmptyStrengthObj = { title: "", content: "" };
export const EmptySpecialityObj = { title: "", content: "" };
export const EmptyCourseObj = {
  name: "",
  desc: "",
  isOnline: false,
  meetingUrl: "",
  address: "",
  startDate: "",
  startTime: "",
  duration: 0.5,
};
export const EmptyArticleObj = { title: "", content: "" };
export const EmptyPatientReviewObj = {
  patientName: "",
  title: "",
  content: "",
};
export const EmptyColleagueObj = {
  firstName: "",
  lastName: "",
  image: null,
  desc: "",
};
export const EmptySharingObj = { title: "", content: "", attachments: [] };

export const TitleOptions: any[] = [
  { id: TitleOption.ACUPUNCTURIST, name: t("ACUPUNCTURIST") },
  { id: TitleOption.MASSEUR, name: t("MASSEUR") },
  {
    id: TitleOption.CN_MEDICINE_PRACTITIONER,
    name: t("CN_MEDICINE_PRACTITIONER"),
  },
];

export const GetEmptyContent = (isEditMode: boolean, customisedString?: string) => {
  if (isEditMode) return t('NO_INPUT_YET');
  return customisedString ?? "";
};